import { createDomain } from 'effector'
import { useStoreMap } from 'effector-react'
import { z } from 'zod'
import { AggregatorDriver, AxiosErrorType } from '~/shared/api'
import { createCache } from '~/shared/lib/mapCacheFactory'

export const aggregatorDriverSchema = AggregatorDriver.schema.pick({
  blockOrdersOnBalanceBelowLimit: true,
  partner: true,
  platform: true,
  driverId: true,
  accountId: true,
})

export type SchemaType = z.infer<typeof aggregatorDriverSchema>

export type FormValues = SchemaType

export const domain = createDomain(
  'entities.driver.aggregator.aggregator-driver',
)

export const requestFx = domain.createEffect<UniqueId, AggregatorDriver>({
  handler: fetchAggregatorDriver,
})

export const saveFx = domain.createEffect<
  AggregatorDriver,
  AggregatorDriver,
  AxiosErrorType
>({
  async handler(aggregator) {
    await aggregator.save()
    return fetchAggregatorDriver(aggregator.getApiId() as UniqueId)
  },
})

const {
  $cache: $aggregatorDriverCache,
  useCache: useAggregatorDriverCache,
  updateCache,
} = createCache<AggregatorDriver>({
  domain,
  getEntityId: (aggregator) => aggregator.getApiId() as UniqueId,
})
export { $aggregatorDriverCache, useAggregatorDriverCache }

$aggregatorDriverCache
  .on(requestFx.doneData, (cache, aggregator) =>
    updateCache(cache, [aggregator]),
  )
  .on(saveFx.doneData, (cache, aggregator) =>
    updateCache(cache, [aggregator], true),
  )

export const $aggregatorDriverError = domain
  .createStore<Record<UniqueId, Error>>({})
  .on([requestFx.fail], (store, { error, params: id }) => ({
    [id]: error,
    ...store,
  }))

export const useAggregatorDriverError = (id: UniqueId) =>
  useStoreMap($aggregatorDriverError, (errors) => errors[id])

async function fetchAggregatorDriver(id: UniqueId) {
  const response = await AggregatorDriver.with('aggregatorAccount')
    .with('yandexWorkRule')
    .with('aggregatorOccupation')
    .find(id)
  return response.getData() as AggregatorDriver
}
